"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.adjustRateTimeForRange = void 0;
const dayjs = require("dayjs");
const customParseFormat = require("dayjs/plugin/customParseFormat");
dayjs.extend(customParseFormat);
const minMax = require("dayjs/plugin/minMax");
dayjs.extend(minMax);
const isSameOrAfter = require("dayjs/plugin/isSameOrAfter");
dayjs.extend(isSameOrAfter);
// Helper function to parse time string into dayjs object
const parseTime = (time) => {
    return dayjs(time, "HH:mm");
};
// Helper function to format dayjs object back into HH:mm string
const formatTime = (date) => {
    return date.format("HH:mm");
};
// Function to check if two ranges overlap
const isOverlap = (range1, range2) => {
    const start1 = parseTime(range1.start_time);
    const end1 = parseTime(range1.end_time);
    const start2 = parseTime(range2.start_time);
    const end2 = parseTime(range2.end_time);
    return start1.isSameOrBefore(end2) && end1.isSameOrAfter(start2);
};
// Function to get the difference in minutes between two dayjs objects
const getDifferenceInMinutes = (start, end) => {
    return end.diff(start, "minute");
};
// Main function to adjust rate times
const adjustRateTimeForRange = (tee_time_range, rate) => {
    const teeRangeStart = parseTime(tee_time_range.start_time);
    const teeRangeEnd = parseTime(tee_time_range.end_time);
    const rateStart = parseTime(rate.earliest_time);
    const rateEnd = parseTime(rate.latest_time);
    // Calculate the duration of the rate range
    const rateDuration = getDifferenceInMinutes(rateStart, rateEnd);
    let newEarliestTime = rateStart;
    let newLatestTime = rateEnd;
    // Special case: If rate duration is less than 60 minutes, set result to be rate range itself
    if (rateDuration < 60) {
        return {
            ...rate,
            earliest_time: formatTime(rateStart),
            latest_time: formatTime(rateEnd),
        };
    }
    // Case 1: No overlap and tee_time_range starts after rate.latest_time
    if (!isOverlap(tee_time_range, {
        start_time: formatTime(rateStart),
        end_time: formatTime(rateEnd),
    }) &&
        teeRangeStart.isAfter(rateEnd)) {
        newEarliestTime = rateEnd.subtract(1, "hour");
        newLatestTime = rateEnd;
        // Adjust to ensure duration is at least 1 hour but within rate range
        if (getDifferenceInMinutes(newEarliestTime, newLatestTime) < 60) {
            newEarliestTime = newLatestTime.subtract(60, "minutes");
        }
    }
    // Case 2: No overlap and tee_time_range ends before rate.earliest_time
    if (!isOverlap(tee_time_range, {
        start_time: formatTime(rateStart),
        end_time: formatTime(rateEnd),
    }) &&
        teeRangeEnd.isBefore(rateStart)) {
        newEarliestTime = rateStart;
        newLatestTime = rateStart.add(1, "hour");
        // Ensure the new latest time does not exceed the rate's latest time
        if (newLatestTime.isAfter(rateEnd)) {
            newLatestTime = rateEnd;
            newEarliestTime = newLatestTime.subtract(60, "minutes");
        }
    }
    // Case 3: Overlap handling
    if (isOverlap(tee_time_range, {
        start_time: formatTime(rateStart),
        end_time: formatTime(rateEnd),
    })) {
        newEarliestTime = dayjs.max([rateStart, teeRangeStart]);
        newLatestTime = dayjs.min([rateEnd, teeRangeEnd]);
        // Ensure the duration is at least 60 minutes but not more than 1 hour or rate's available range
        const overlapDuration = getDifferenceInMinutes(newEarliestTime, newLatestTime);
        if (overlapDuration < 60) {
            newEarliestTime = dayjs.max([rateStart, newEarliestTime]);
            newLatestTime = newEarliestTime.add(Math.min(60, rateDuration), "minutes");
            // Ensure the newLatestTime does not exceed the rate's latest time
            if (newLatestTime.isAfter(rateEnd)) {
                newLatestTime = rateEnd;
                newEarliestTime = newLatestTime.subtract(60, "minutes");
            }
        }
    }
    // Final check to ensure the output duration does not exceed the rate range duration
    const finalDuration = getDifferenceInMinutes(newEarliestTime, newLatestTime);
    if (finalDuration > rateDuration) {
        newLatestTime = newEarliestTime.add(rateDuration, "minutes");
    }
    return {
        ...rate,
        earliest_time: formatTime(newEarliestTime),
        latest_time: formatTime(newLatestTime),
    };
};
exports.adjustRateTimeForRange = adjustRateTimeForRange;
